import {
  Box,
  Typography,
  Avatar,
  useTheme,
  Stack,
  Paper,
  IconButton,
} from "@mui/material";
import { getVariantConfig } from "Components/Shared/Notification/utils";
import type { NotificationVariant } from "Infrastructure/Api/Api";
import { Link } from "Navigation/Link";
import type { ReactNode } from "react";
import { useSwipeToDismiss } from "../../../Hooks/useSwipeToDismiss";
import CloseIcon from "@mui/icons-material/Close";
import { useIsMobile } from "Hooks/useIsMobile";

type Props = {
  name: string;
  content: ReactNode;
  variant: NotificationVariant;
  time?: string;
  withPaper?: boolean;
  controls?: ReactNode;
  onClose?: () => void;
  isDimmed?: boolean;
  /** Relative in-app route to open when the notification body is clicked. */
  link?: string | null;
};

export const Notification = ({ withPaper, ...restProps }: Props) => {
  const theme = useTheme();

  const config = getVariantConfig(restProps.variant, theme);

  if (withPaper) {
    return (
      <Paper
        sx={{
          p: 1,
          pl: 2,
          borderRadius: "10px",
          boxShadow: `0 0 5px 0 ${theme.palette.grey[200]}`,
          bgcolor: config.backgroundColor,
        }}
      >
        <NotificationContent {...restProps} />
      </Paper>
    );
  }

  return <NotificationContent {...restProps} />;
};

const NotificationContent = ({
  name,
  content,
  time,
  variant,
  controls,
  onClose,
  isDimmed = false,
  link,
}: Props) => {
  const theme = useTheme();
  const isMobile = useIsMobile();

  const {
    backgroundColor,
    iconBackgroundColor,
    icon,
    textColor,
    secondaryTextColor,
  } = getVariantConfig(variant, theme);

  const dismissMode = !onClose ? "disabled" : isMobile ? "swipe" : "click";
  const { dragEventHandlers, draggedItemStyles, handleDismiss } =
    useSwipeToDismiss({
      onDismiss: onClose,
      mode: dismissMode,
    });

  return (
    <Stack sx={{ overflow: "hidden" }}>
      <Box
        p={2}
        gap={3}
        display="flex"
        alignItems="center"
        position="relative"
        borderRadius="10px"
        bgcolor={backgroundColor}
        {...dragEventHandlers}
        sx={{
          ...draggedItemStyles,
          opacity: isDimmed ? 0.5 : draggedItemStyles.opacity,
        }}
      >
        <Avatar sx={{ width: 36, height: 36, bgcolor: iconBackgroundColor }}>
          {icon}
        </Avatar>
        {/* A navigating body must be a real anchor — keyboard focus and screen readers don't see
            an onClick on a Stack. */}
        {link ? (
          <Stack
            gap={0.5}
            flexGrow={1}
            component={Link}
            to={link}
            sx={{ color: "inherit", textDecoration: "none" }}
          >
            <NotificationBody
              name={name}
              time={time}
              content={content}
              textColor={textColor}
              secondaryTextColor={secondaryTextColor}
            />
          </Stack>
        ) : (
          <Stack gap={0.5} flexGrow={1}>
            <NotificationBody
              name={name}
              time={time}
              content={content}
              textColor={textColor}
              secondaryTextColor={secondaryTextColor}
            />
          </Stack>
        )}
        {onClose && (
          <IconButton
            size="small"
            onClick={handleDismiss}
            sx={{
              alignSelf: "flex-start",
              color: secondaryTextColor,
            }}
          >
            <CloseIcon sx={{ width: "0.875rem", height: "0.875rem" }} />
          </IconButton>
        )}
      </Box>
      {controls}
    </Stack>
  );
};

type NotificationBodyProps = {
  name: string;
  content: ReactNode;
  time?: string;
  textColor: string;
  secondaryTextColor: string;
};

const NotificationBody = ({
  name,
  content,
  time,
  textColor,
  secondaryTextColor,
}: NotificationBodyProps) => (
  <>
    <Box display="flex" alignItems="center" flexWrap="wrap" columnGap={2}>
      <Typography
        fontWeight={600}
        variant="subtitle2"
        sx={{ wordBreak: "break-word" }}
        lineHeight="unset"
        textTransform="uppercase"
        color={textColor}
      >
        {name}
      </Typography>
      {time && (
        <Typography color={secondaryTextColor} fontSize={12}>
          {time}
        </Typography>
      )}
    </Box>
    <Typography
      component="div"
      variant="body2"
      color={textColor}
      sx={{ wordBreak: "break-word" }}
    >
      {content}
    </Typography>
  </>
);
